/**
 * Product names, as a person would read them.
 *
 * Retail catalogue names are written for a warehouse. What comes back from the crawl is
 *
 *     Glacier Bay 25 in. Single Sink White Freestanding Bath Vanity with White Cultured
 *     Marble Top (Assembled) GB24P2-WH
 *
 * and the rail then renders "Glacier Bay" a second time underneath as the brand. On a 320-px
 * phone that is five lines of type, of which two are the manufacturer's filing system, in a
 * product whose whole job is to help somebody choose how their bathroom will look. Nine of
 * those cards is a spreadsheet.
 *
 * So the rail shows a trimmed title and nothing else changes. **The stored name is never
 * touched**, and every surface where exactness is what matters keeps it in full:
 *
 * * the list the homeowner sends and the packet Andrew orders from (`packet.py`);
 * * the store link on every card, which goes to that exact page;
 * * the `title` attribute on the heading, so the full name is one hover away.
 *
 * The trimming is conservative by design — three rules, each anchored to the end of the string
 * or to the brand, and every one of them is reversible by looking at the card.
 */

/**
 * A trailing manufacturer's model number.
 *
 * Five characters or more, containing a digit, and either shouted or hyphenated: `GB24P2-WH`,
 * `PHXCF209NS`, `SD-AZ050-01CH`, `21-2-36-00`, `HTH0002`. Deliberately not `48WT` (too short to
 * be sure it is not a size) and not `16'` or `3/4` (no letters, no hyphen — those are
 * dimensions, and dropping a dimension would change what the product *is*).
 */
const MODEL_SUFFIX = /\s+(?=[^\s]*\d)(?=[^\s]*(?:[A-Z]|-))[A-Z0-9][A-Z0-9./-]{4,}$/

/** A trailing cross-reference to another supplier's catalogue: `(Gs#Wm231)`, `(Similar To G/S #Wm1021)`. */
const CROSS_REFERENCE = /\s*\([^()]*#[^()]*\)\s*$/

/** Warehouse notes that say nothing about how a thing looks. */
const NOISE = /\s*\((?:assembled|unassembled|ready to assemble|rta)\)\s*/gi

/**
 * The name to show while somebody is choosing.
 *
 * `brand` is dropped from the front only when the rail is showing it separately — passing it is
 * what says so. Everything else is unconditional.
 */
export function productTitle(name: string, brand?: string | null): string {
  let title = name.trim()

  if (brand) {
    const prefix = brand.trim()
    if (prefix && title.toLowerCase().startsWith(prefix.toLowerCase())) {
      const rest = title.slice(prefix.length).trim()
      // Only if something substantial is left. "Bruce" alone is a better card than "".
      if (rest.length > 8) title = rest
    }
  }

  title = title.replace(NOISE, ' ')
  title = title.replace(CROSS_REFERENCE, '')
  title = title.replace(MODEL_SUFFIX, '')

  return title.replace(/\s{2,}/g, ' ').trim() || name.trim()
}
